// Service page data for all services

export const trademarkRegistrationData = {
  title: "Trademark Registration",
  description: "Secure federal protection for your brand name, logo, or slogan with expert trademark registration services",
  overview: [
    "Trademark registration is a critical step in protecting your brand identity and establishing exclusive rights to your business name, logo, or slogan. Our comprehensive trademark registration services ensure your intellectual property receives full federal protection under USPTO guidelines.",
    "With over 15 years of experience in trademark law, our team of licensed attorneys and trademark specialists guide you through every step of the registration process. From conducting thorough trademark searches to filing applications and responding to office actions, we handle the complexities so you can focus on building your business.",
    "A registered trademark provides nationwide protection, prevents others from using similar marks, and adds substantial value to your business assets. Whether you're launching a new brand or protecting an established one, our trademark registration services provide the legal foundation for your brand's success."
  ],
  benefits: [
    {
      title: "Exclusive Rights",
      description: "Gain exclusive nationwide rights to use your trademark in connection with your goods or services"
    },
    {
      title: "Legal Protection",
      description: "Strong legal presumption of ownership and validity in case of disputes or infringement"
    },
    {
      title: "Brand Value",
      description: "Increase your brand's value and credibility with customers, investors, and partners"
    },
    {
      title: "Online Protection",
      description: "Easier to take down infringing content on social media and e-commerce platforms"
    },
    {
      title: "Business Asset",
      description: "Trademark becomes a valuable business asset that can be licensed or sold"
    },
    {
      title: "Deterrent Effect",
      description: "Public registration deters others from adopting similar marks"
    }
  ],
  process: [
    {
      title: "Initial Consultation",
      description: "We discuss your trademark needs, review your brand elements, and determine the best protection strategy"
    },
    {
      title: "Comprehensive Search",
      description: "Our team conducts extensive searches of federal, state, and common law databases to identify potential conflicts"
    },
    {
      title: "Application Preparation",
      description: "We prepare and file your trademark application with detailed descriptions and proper classifications"
    },
    {
      title: "USPTO Filing",
      description: "Your application is filed electronically with the United States Patent and Trademark Office"
    },
    {
      title: "Monitoring & Response",
      description: "We monitor your application status and respond to any office actions or examiner inquiries"
    },
    {
      title: "Registration & Protection",
      description: "Once approved, you receive your trademark registration certificate and ongoing monitoring"
    }
  ],
  faqs: [
    {
      question: "How long does trademark registration take?",
      answer: "The typical timeline is 8-12 months from filing to registration, though it can vary based on USPTO workload and whether any office actions are issued."
    },
    {
      question: "What can be trademarked?",
      answer: "You can trademark brand names, logos, slogans, product names, service names, and even certain colors or sounds associated with your brand."
    },
    {
      question: "Do I need a trademark if I have an LLC?",
      answer: "Yes. Forming an LLC protects your business entity but doesn't protect your brand name. A trademark specifically protects your brand identity."
    },
    {
      question: "What's the difference between TM and ®?",
      answer: "TM indicates a trademark claim but not registration. ® can only be used after your trademark is officially registered with the USPTO."
    },
    {
      question: "How long does a trademark last?",
      answer: "Trademarks can last indefinitely as long as you continue using them and file the required maintenance documents every 10 years."
    }
  ]
};

export const copyrightRegistrationData = {
  title: "Copyright Registration",
  description: "Protect your original creative works with official copyright registration and comprehensive legal protection",
  overview: [
    "Copyright registration provides essential legal protection for your original creative works, including written content, artwork, music, software, and other creative expressions. While copyright exists automatically upon creation, federal registration provides significant legal advantages and is required before filing an infringement lawsuit.",
    "Our copyright registration services streamline the process of securing federal protection for your intellectual property. We handle all aspects of the registration process, from preparing detailed applications to responding to Copyright Office inquiries, ensuring your creative works receive the strongest possible protection.",
    "With registered copyright, you gain the exclusive right to reproduce, distribute, display, and create derivative works based on your original creation. This protection is essential for authors, artists, musicians, software developers, and any creator seeking to safeguard their intellectual property."
  ],
  benefits: [
    {
      title: "Legal Presumption",
      description: "Creates public record and legal presumption of ownership and validity of your copyright"
    },
    {
      title: "Lawsuit Eligibility",
      description: "Required before you can file an infringement lawsuit in federal court"
    },
    {
      title: "Statutory Damages",
      description: "Eligibility for statutory damages and attorney's fees in infringement cases"
    },
    {
      title: "Customs Protection",
      description: "Registration with U.S. Customs to prevent importation of infringing copies"
    },
    {
      title: "Public Notice",
      description: "Public record provides notice of your copyright claim to potential infringers"
    },
    {
      title: "International Protection",
      description: "Establishes basis for international copyright protection in many countries"
    }
  ],
  process: [
    {
      title: "Work Assessment",
      description: "We review your creative work and determine the appropriate registration type and classification"
    },
    {
      title: "Application Preparation",
      description: "We prepare your copyright application with complete and accurate information"
    },
    {
      title: "Deposit Submission",
      description: "We submit the required deposit of your work along with the application"
    },
    {
      title: "Copyright Office Filing",
      description: "Your application is filed with the U.S. Copyright Office"
    },
    {
      title: "Examination Process",
      description: "We handle any correspondence with the Copyright Office during examination"
    },
    {
      title: "Certificate Delivery",
      description: "You receive your official Copyright Registration Certificate"
    }
  ],
  faqs: [
    {
      question: "What can be copyrighted?",
      answer: "Original works of authorship including literary works, musical compositions, artwork, photographs, films, software code, and architectural designs can be copyrighted."
    },
    {
      question: "How long does copyright registration take?",
      answer: "Standard processing typically takes 6-8 months, though expedited processing is available for an additional fee."
    },
    {
      question: "Do I need to register my copyright?",
      answer: "While copyright exists automatically, registration is highly recommended as it's required to sue for infringement and provides significant legal advantages."
    },
    {
      question: "How long does copyright protection last?",
      answer: "For works created after 1978, copyright typically lasts for the author's life plus 70 years."
    },
    {
      question: "Can I copyright an idea?",
      answer: "No, copyright protects the expression of ideas, not the ideas themselves. Ideas, concepts, and facts cannot be copyrighted."
    }
  ]
};

export const trademarkSearchData = {
  title: "Trademark Comprehensive Search",
  description: "Thorough trademark availability search to identify potential conflicts before filing your application",
  overview: [
    "A comprehensive trademark search is a critical first step before investing in trademark registration. Our extensive search process examines federal, state, and common law databases to identify existing trademarks that could conflict with your proposed mark, potentially saving you thousands of dollars in rebranding costs and legal fees.",
    "Our experienced trademark attorneys and researchers conduct multi-level searches that go far beyond basic USPTO database queries. We analyze phonetic similarities, visual similarities, and conceptual similarities across multiple classes of goods and services to provide you with a thorough risk assessment.",
    "With our comprehensive search report, you'll receive detailed findings, professional analysis, and strategic recommendations about your trademark's registrability and potential conflicts. This invaluable information allows you to make informed decisions about your brand before committing to the registration process."
  ],
  benefits: [
    {
      title: "Risk Mitigation",
      description: "Identify potential conflicts before investing in branding and trademark registration"
    },
    {
      title: "Cost Savings",
      description: "Avoid expensive rebranding and legal disputes by discovering conflicts early"
    },
    {
      title: "Strategic Planning",
      description: "Make informed decisions about trademark strategy and brand development"
    },
    {
      title: "Comprehensive Coverage",
      description: "Search federal, state, common law, and domain name databases"
    },
    {
      title: "Professional Analysis",
      description: "Receive expert legal analysis and recommendations from trademark attorneys"
    },
    {
      title: "Faster Registration",
      description: "Reduce likelihood of office actions and objections from USPTO"
    }
  ],
  process: [
    {
      title: "Mark Submission",
      description: "You provide your proposed trademark and description of goods/services"
    },
    {
      title: "Database Searches",
      description: "We search USPTO, state registrations, common law sources, and domain databases"
    },
    {
      title: "Conflict Analysis",
      description: "Our attorneys analyze results for phonetic, visual, and conceptual similarities"
    },
    {
      title: "Report Preparation",
      description: "We compile comprehensive findings with risk assessment and recommendations"
    },
    {
      title: "Attorney Review",
      description: "Licensed trademark attorney reviews and validates all findings"
    },
    {
      title: "Delivery & Consultation",
      description: "You receive detailed report and consultation to discuss findings and next steps"
    }
  ],
  faqs: [
    {
      question: "Why do I need a comprehensive search?",
      answer: "A comprehensive search identifies potential conflicts that could result in application rejection, opposition proceedings, or costly infringement disputes."
    },
    {
      question: "What does a comprehensive search include?",
      answer: "It includes searches of federal registrations, state registrations, common law uses, business registrations, domain names, and social media handles."
    },
    {
      question: "How long does the search take?",
      answer: "Most comprehensive searches are completed within 3-5 business days."
    },
    {
      question: "What if conflicts are found?",
      answer: "We provide strategic recommendations which may include modifying your mark, pursuing registration in specific classes, or selecting an alternative mark."
    },
    {
      question: "Is a search required before filing?",
      answer: "While not legally required, it's highly recommended to avoid wasting time and money on marks that can't be registered."
    }
  ]
};

export const companyFormationData = {
  title: "Company Formation",
  description: "Establish your business entity with proper legal structure and compliance",
  overview: [
    "Proper company formation is the foundation of every successful business. Our comprehensive company formation services help you establish the right legal structure for your business, whether you're launching a startup, formalizing an existing business, or expanding operations. We handle all aspects of entity formation, from choosing the optimal structure to filing necessary documents with state authorities.",
    "Selecting the right business structure—LLC, Corporation, Partnership, or other entity—has significant implications for taxation, liability protection, fundraising capabilities, and operational flexibility. Our experienced business attorneys provide personalized guidance to ensure your company is structured to support your specific business goals and growth plans.",
    "We streamline the formation process, handling all paperwork, state filings, and compliance requirements. From preparing articles of organization to obtaining your EIN, we manage every detail so you can focus on launching and growing your business with confidence."
  ],
  benefits: [
    {
      title: "Liability Protection",
      description: "Separate your personal assets from business liabilities with proper entity structure"
    },
    {
      title: "Tax Advantages",
      description: "Optimize tax treatment with the right business structure for your situation"
    },
    {
      title: "Credibility",
      description: "Enhance business credibility with customers, vendors, and financial institutions"
    },
    {
      title: "Fundraising",
      description: "Better positioned to raise capital from investors or obtain business loans"
    },
    {
      title: "Compliance",
      description: "Ensure proper compliance with state and federal regulations from day one"
    },
    {
      title: "Growth Foundation",
      description: "Establish scalable structure that supports business growth and expansion"
    }
  ],
  process: [
    {
      title: "Business Consultation",
      description: "We discuss your business goals, operations, and determine the optimal entity structure"
    },
    {
      title: "Entity Selection",
      description: "Choose between LLC, Corporation, Partnership, or other structure based on your needs"
    },
    {
      title: "Name Availability",
      description: "We verify your business name availability and reserve it with the state"
    },
    {
      title: "Document Preparation",
      description: "Prepare articles of organization/incorporation and all required formation documents"
    },
    {
      title: "State Filing",
      description: "File formation documents with the appropriate state authority"
    },
    {
      title: "EIN & Compliance",
      description: "Obtain federal EIN and complete initial compliance requirements"
    }
  ],
  faqs: [
    {
      question: "Should I form an LLC or Corporation?",
      answer: "LLCs offer simpler management and tax flexibility, while Corporations are better for raising venture capital and going public. We'll help you choose based on your specific situation."
    },
    {
      question: "How long does company formation take?",
      answer: "Processing time varies by state but typically ranges from 1-3 weeks. Expedited filing is available in most states."
    },
    {
      question: "Do I need a registered agent?",
      answer: "Yes, most states require a registered agent to receive legal documents on behalf of your business. We can serve as your registered agent."
    },
    {
      question: "What's the difference between articles and bylaws?",
      answer: "Articles of organization/incorporation are filed with the state to create the entity. Bylaws are internal rules governing company operations."
    },
    {
      question: "Do I need an attorney to form a company?",
      answer: "While not required, professional guidance ensures proper structure, compliance, and protection of your interests from the start."
    }
  ]
};

export const amazonBrandRegistryData = {
  title: "Amazon Brand Registry",
  description: "Protect and enhance your brand presence on Amazon's marketplace with official brand registry",
  overview: [
    "Amazon Brand Registry is a powerful program that provides enhanced brand protection and marketing tools for sellers on the world's largest e-commerce platform. Registration requires an active trademark and unlocks valuable features including proactive brand protection, enhanced content tools, and increased control over your product listings.",
    "Our Amazon Brand Registry service handles the complete enrollment process, from ensuring your trademark meets requirements to navigating Amazon's verification procedures. With registered trademark expertise and Amazon marketplace knowledge, we streamline what can be a complex and confusing process.",
    "Once enrolled, you gain access to powerful tools to protect your brand from counterfeiters, create enhanced brand content, and control how your products appear on Amazon. This is essential for any serious Amazon seller looking to build and protect their brand on the platform."
  ],
  benefits: [
    {
      title: "Brand Protection",
      description: "Proactive monitoring and automated protections against counterfeit and infringing listings"
    },
    {
      title: "Enhanced Content",
      description: "Create rich product descriptions with A+ Content and brand stores"
    },
    {
      title: "Search Benefits",
      description: "Improved search placement and increased visibility for your branded products"
    },
    {
      title: "Reporting Tools",
      description: "Access to reporting tools to identify and remove infringing or counterfeit products"
    },
    {
      title: "Sponsored Brands",
      description: "Unlock sponsored brands advertising opportunities for increased visibility"
    },
    {
      title: "Brand Analytics",
      description: "Access valuable insights about customer search behavior and competitor activity"
    }
  ],
  process: [
    {
      title: "Trademark Verification",
      description: "We verify your trademark meets Amazon's requirements or help you obtain one"
    },
    {
      title: "Account Preparation",
      description: "Ensure your Amazon Seller Central account is properly configured for enrollment"
    },
    {
      title: "Application Submission",
      description: "Complete and submit the Brand Registry application with all required information"
    },
    {
      title: "Verification Process",
      description: "Navigate Amazon's verification procedures and respond to any requests"
    },
    {
      title: "Enrollment Completion",
      description: "Finalize enrollment and configure initial brand protection settings"
    },
    {
      title: "Tool Training",
      description: "Receive guidance on using Brand Registry tools effectively"
    }
  ],
  faqs: [
    {
      question: "Do I need a trademark for Amazon Brand Registry?",
      answer: "Yes, an active registered trademark is required. We can help you obtain trademark registration if you don't already have one."
    },
    {
      question: "How long does Amazon Brand Registry enrollment take?",
      answer: "Once your trademark is registered, the Brand Registry enrollment typically takes 1-2 weeks, though it can vary."
    },
    {
      question: "Can I enroll multiple brands?",
      answer: "Yes, you can enroll multiple brands as long as you have registered trademarks for each brand name."
    },
    {
      question: "What if I'm already selling on Amazon?",
      answer: "Existing sellers can enroll in Brand Registry at any time. Your existing listings will be associated with your brand."
    },
    {
      question: "Is Brand Registry worth it for small sellers?",
      answer: "Absolutely. Brand Registry provides valuable protection and tools regardless of seller size, helping prevent hijacking and improve listings."
    }
  ]
};
